'use client'

import Link from 'next/link'
import Image from 'next/image'
import Script from 'next/script'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faHeadset, faPhone, faLocationDot, faBuilding, faUserTie, faClock, faIdCard } from '@fortawesome/free-solid-svg-icons'
import { 
  faFacebookF, 
  faTwitter, 
  faYoutube, 
  faTelegram,
  faPinterest,
  faLinkedinIn,
  faTwitch,
  faSoundcloud,
  faVimeo,
  faQuora,
  faReddit,
  fa500px,
  faTiktok
} from '@fortawesome/free-brands-svg-icons'
import { BRAND } from '@/config/domain.config'

interface Page {
  id: string
  title: string
  slug: string
  uri: string
}

interface ClientFooterProps {
  pages: Page[]
}

const links = [
  { name: 'Bảng xếp hạng', href: '/bang-xep-hang-bong-da' },
  { name: 'Lịch thi đấu', href: '/lich-thi-dau-bong-da' },
  { name: 'Kết quả bóng đá', href: '/kqbd' },
  { name: 'Highlights', href: '/highlights' },
  { name: 'Tin tức', href: '/tin-bong-da' },
  { name: 'Nhận định', href: '/nhan-dinh-bong-da' },
]

const contacts = [
  { 
    icon: faBuilding, 
    text: (
      <span className="address-container">
        Thương hiệu: <Link href="/" className="text-[var(--primary)] hover:text-white transition-colors whitespace-nowrap">
         Xoilac
        </Link>
      </span>
    )
  },
  { 
    icon: faUserTie, 
    text: (
      <>
        Người đại diện:<Link href="/ceo-xoilac-tv-dinh-hoang-nam" className="text-[var(--primary)] hover:text-white transition-colors">Đinh Hoàng Nam</Link>
      </>
    )
  },
  { 
    icon: faLocationDot, 
    text: (
      <span className="address-container">
        <a 
          href="https://www.google.com/maps/place/xoilac/@21.024124,105.819702,17z/data=!3m1!4b1!4m6!3m5!1s0x3135ab968b6d2ebb:0x5a931a30263571d8!8m2!3d21.024124!4d105.819702!16s%2Fg%2F11lw7mh3kk?hl=vi&entry=ttu&g_ep=EgoyMDI1MDUxNS4wIKXMDSoASAFQAw%3D%3D"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--primary)] hover:text-white transition-colors"
        >
          Địa chỉ: 67 Đ. Miếu Bình Đông, Bình Hưng Hoà A, Bình Tân, Hồ Chí Minh, Việt Nam
        </a>
      </span>
    )
  },
  { icon: faPhone, text: 'Phone: +84983965879' },
  { icon: faEnvelope, text: 'Email: contact@xoilactv.tech' },
  { icon: faClock, text: 'Thời gian hoạt động: 24/7' },
]

const socialLinks = [
  { icon: faTwitter, href: 'https://x.com/xoilactvtech12', name: 'Twitter' },
  { icon: faPinterest, href: 'https://www.pinterest.com/xoilactvtech12/', name: 'Pinterest' },
  { icon: faYoutube, href: 'https://www.youtube.com/@xoilactvtech12', name: 'YouTube' },
  { icon: faTwitch, href: 'https://www.twitch.tv/xoilactvtech1/', name: 'Twitch' },
  { icon: faVimeo, href: 'https://vimeo.com/xoilactvtech12', name: 'Vimeo' },
  { icon: faQuora, href: 'https://www.quora.com/profile/Xoilac-TV-321', name: 'Quora' },
  { icon: faReddit, href: 'https://www.reddit.com/user/xoilactvtech12/', name: 'Reddit' },
  { icon: fa500px, href: 'https://500px.com/p/xoilactvtech12', name: '500px' }
]

export default function ClientFooter({ pages }: ClientFooterProps) {
  return (
    <footer className="bg-[#000000] pt-15 pb-8 mt-15 border-t border-white/10">
      <div className="container mx-auto px-4 pt-4">
        <div className="grid gap-10 mb-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-5">
              <Link href="/">
                <Image
                  src="/images/logo.png"
                  alt={BRAND.NAME}
                  width={200}
                  height={66}
                  className="w-[180px] h-auto"
                  quality={100}
                  suppressHydrationWarning
                />
              </Link>
            </div>
            <div itemScope itemType="https://schema.org/SportsOrganization">
              <p className="text-gray-400 text-sm mb-4">
              <Link href="/" className="text-[var(--primary)] hover:text-white transition-colors">
               Xoilac
              </Link> là nền tảng xem trực tiếp bóng đá uy tín, được đông đảo người hâm mộ tin tưởng. Chúng tôi cung cấp nội dung chất lượng, cập nhật nhanh lịch thi đấu, kết quả, tỷ lệ kèo và link xem bóng đá mượt, ổn định. 
              <Link href="/" className="text-[var(--primary)] hover:text-white transition-colors">
                Xoilac TV
              </Link> cam kết mang đến trải nghiệm xem bóng đá có bản quyền, miễn phí và không quảng cáo.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Theo dõi Xoilac TV trên ${link.name}`}
                  title={`Theo dõi Xoilac TV trên ${link.name}`}
                  className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[var(--primary)] hover:transform hover:-translate-y-0.5 transition-all"
                >
                  <FontAwesomeIcon icon={link.icon} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Thông tin chung
            </h3>
            <ul className="space-y-3">
              {pages.map((page: Page) => (
                <li key={page.id}>
                  <Link
                    href={`/${page.slug}`}
                    className="text-gray-400 text-sm hover:text-[var(--primary)] hover:translate-x-1.5 transition-all flex items-center before:content-['\203A'] before:mr-2 before:text-[var(--primary)] before:text-lg before:leading-none"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Liên kết
            </h3>
            <ul className="space-y-3">
              {links.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-gray-400 text-sm hover:text-[var(--primary)] hover:translate-x-1.5 transition-all flex items-center before:content-['\203A'] before:mr-2 before:text-[var(--primary)] before:text-lg before:leading-none"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Liên hệ
            </h3>
            <ul className="space-y-4">
              {contacts.map((contact, index) => (
                <li key={index} className="flex items-center gap-2.5 text-gray-400 text-sm">
                  <span className="w-[30px] h-[30px] bg-white/10 rounded-full flex items-center justify-center text-[var(--primary)] flex-shrink-0">
                    <FontAwesomeIcon icon={contact.icon} className="text-sm" />
                  </span>
                  {contact.text}
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row md:justify-between md:items-center gap-4 text-center md:text-left">
          <div className="text-gray-400 text-sm">
            © 2024 {BRAND.COPYRIGHT}. All rights reserved.
          </div>
          {/* DMCA Protection Badge */}
          <div className="flex justify-center">
            <a 
              href="https://www.dmca.com/r/jpjlq66" 
              title="DMCA.com Protection Status" 
              className="dmca-badge"
              target="_blank"
              rel="noopener noreferrer"
              suppressHydrationWarning
            >
              <Image
                src="https://images.dmca.com/Badges/_dmca_premi_badge_4.png?ID=191471a2-ebeb-4bda-9470-4c7e6b432b3d"
                alt="DMCA.com Protection Status"
                width={120}
                height={30}
                className="h-auto hover:opacity-80 transition-opacity"
                suppressHydrationWarning
              />
            </a>
              {/* Load script từ bên ngoài */}
      <Script
        src="//s1.what-on.com/widget/service.js?key=ZAJP5a"
        strategy="afterInteractive" // chạy sau khi trang đã load
      />
      <div id="ZAJP5a" style={{ textAlign: 'center' }}></div>
          </div>
          
          <div className="flex items-center justify-center gap-4 text-sm">
            <Link href="/chinh-sach-bao-mat" className="text-gray-400 hover:text-[var(--primary)] transition-colors">
              Chính sách bảo mật
            </Link>
            <span className="text-gray-600">|</span>
            <Link href="/dieu-khoan-su-dung" className="text-gray-400 hover:text-[var(--primary)] transition-colors">
              Điều khoản sử dụng
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
} 